/**
 * "Agent activity" panel (docked in the agent drawer): the ledger list with
 * attribution ("Agent · add_chords · 2s ago") and a per-entry Undo button that
 * runs the entry's undo closure (the tools record `store.undoTo(id)` there).
 *
 *   const panel = mountLedgerPanel(container);
 *   panel.dispose(); // unsubscribes and clears the timestamp refresher
 */
import { ledger, type LedgerEntry } from '../webmcp/ledger';

/** How often the relative timestamps are refreshed, in ms. */
export const LEDGER_TICK_MS = 5000;

function relTime(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
}

const WHO: Record<LedgerEntry['source'], string> = { agent: 'Agent', inspector: 'Inspector', human: 'You' };

const DECLINED_REASONS: Record<string, string> = {
  human_declined: 'declined: you kept your own notes',
  top_is_human_action: 'refused: top entry is your own action',
  human_action_in_range: 'refused: one of your own actions is in the way',
  nothing_to_undo: 'nothing to undo',
  nothing_to_change: 'nothing to change',
  not_recording: 'no recording armed',
  neither: 'audition: you kept neither',
  stopped: 'audition stopped',
};

/**
 * Readable one-liner for an entry. Results with `applied:false` and structured
 * `busy` errors would otherwise show as raw JSON; the JSON stays available in
 * the expandable detail.
 */
export function readableSummary(e: LedgerEntry): string {
  const r = e.result as Record<string, unknown> | null | undefined;
  if (!r || typeof r !== 'object') return e.summary;
  const err = r.error as { code?: string; message?: string } | undefined;
  if (err && typeof err === 'object' && err.code === 'busy') {
    const msg = err.message ?? '';
    if (msg.includes('audition')) return 'busy: audition open';
    if (msg.includes('recording')) return 'busy: recording armed';
    return `busy: ${msg}`;
  }
  if (r.applied === false) {
    const reason = typeof r.reason === 'string' ? r.reason : '';
    if (reason in DECLINED_REASONS) return DECLINED_REASONS[reason]!;
    return reason ? `declined: ${reason.replace(/_/g, ' ')}` : 'declined';
  }
  return e.summary;
}

/** Mounted ledger panel: root element + dispose. */
export interface LedgerPanelHandle {
  el: HTMLElement;
  dispose(): void;
}

/** Mount the ledger panel into `container`. */
export function mountLedgerPanel(container: HTMLElement): LedgerPanelHandle {
  const root = document.createElement('section');
  root.className = 'card wm-ledger';
  root.setAttribute('aria-labelledby', 'wm-ledger-title');
  root.innerHTML = `
    <div class="wm-ledger__head">
      <h2 id="wm-ledger-title">Agent activity</h2>
      <button type="button" class="btn btn--small" data-action="clear">Clear</button>
    </div>
    <ul class="wm-ledger__list" aria-live="polite"></ul>`;
  const list = root.querySelector<HTMLUListElement>('.wm-ledger__list')!;
  root.querySelector('[data-action=clear]')!.addEventListener('click', () => ledger.clear());

  let current: readonly LedgerEntry[] = [];

  const render = () => {
    list.replaceChildren();
    if (!current.length) {
      const li = document.createElement('li');
      li.className = 'wm-ledger__empty';
      li.textContent = 'No tool calls yet.';
      list.append(li);
      return;
    }
    for (const e of current) {
      const li = document.createElement('li');
      li.className = 'wm-entry' + (e.error ? ' wm-entry--error' : '') + (e.undone ? ' wm-entry--undone' : '');
      li.dataset.entryId = e.id;

      const who = document.createElement('div');
      who.className = 'wm-entry__who';
      who.innerHTML = `<b>${WHO[e.source]}</b> · <code>${e.tool}</code> · <span data-rel>${relTime(e.at)}</span>`;
      who.querySelector('[data-rel]')!.setAttribute('title', new Date(e.at).toLocaleTimeString());

      const summary = document.createElement('div');
      summary.className = 'wm-entry__summary';
      const readable = readableSummary(e);
      summary.textContent = readable;

      li.append(who, summary);

      // When the one-liner was rewritten, keep the raw JSON as an expandable detail.
      if (readable !== e.summary && e.result !== undefined) {
        const details = document.createElement('details');
        details.className = 'wm-entry__detail';
        const label = document.createElement('summary');
        label.textContent = 'detail';
        const pre = document.createElement('pre');
        pre.textContent = JSON.stringify(e.result, null, 1);
        details.append(label, pre);
        li.append(details);
      }

      if (e.undo && !e.undone) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn btn--small wm-entry__undo';
        b.textContent = e.undoLabel ? `Undo: ${e.undoLabel}` : 'Undo';
        b.addEventListener('click', async () => {
          b.disabled = true;
          try {
            await ledger.undo(e.id);
          } catch (err) {
            b.disabled = false;
            console.error('undo failed', err);
          }
        });
        li.append(b);
      } else if (e.undone) {
        const s = document.createElement('span');
        s.className = 'wm-entry__undone';
        s.textContent = 'undone';
        li.append(s);
      }
      list.append(li);
    }
  };

  const unsubscribe = ledger.subscribe((entries) => {
    current = entries;
    render();
  });

  // Keep the relative timestamps fresh without re-rendering everything.
  const tick = window.setInterval(() => {
    const now = Date.now();
    list.querySelectorAll<HTMLElement>('.wm-entry').forEach((li) => {
      const e = current.find((x) => x.id === li.dataset.entryId);
      const rel = li.querySelector('[data-rel]');
      if (e && rel) rel.textContent = relTime(e.at, now);
    });
  }, LEDGER_TICK_MS);

  container.append(root);
  return {
    el: root,
    dispose() {
      window.clearInterval(tick);
      unsubscribe();
      root.remove();
    },
  };
}
